import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo';
import RichTextEditor from '../../components/RichTextEditor';
import { Field, Spinner, PageLoader } from '../../components/ui';
import { storiesApi, mediaApi, storyCategoriesApi } from '../../api/resources';
import { assetUrl } from '../../api/client';
import { useToast } from '../../context/ToastContext';

const EMPTY = {
  title: '',
  excerpt: '',
  body: '',
  featured_image_path: '',
  story_category_id: '',
  is_featured: false,
};

export default function StoryForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();

  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let active = true;
    storyCategoriesApi
      .list()
      .then((cats) => active && setCategories(cats))
      .catch(() => {
        /* non-fatal: category select stays empty */
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    storiesApi
      .get(id)
      .then((s) => {
        if (!active) return;
        setForm({
          title: s.title || '',
          excerpt: s.excerpt || '',
          body: s.body || '',
          featured_image_path: s.featured_image_path || '',
          story_category_id: s.category?.id ? String(s.category.id) : '',
          is_featured: !!s.is_featured,
        });
      })
      .catch(() => toast.error('Could not load story.'))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleImage(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // A story featured image is always an image; backend requires `kind`.
      fd.append('kind', 'image');
      fd.append('alt_text', file.name);
      const media = await mediaApi.upload(fd);
      // MediaFileResource returns the usable URL as `media.url`; fall back gracefully.
      const value = media?.url || media?.path || media?.file_path || '';
      if (!value) throw new Error('Upload succeeded but no URL was returned.');
      set('featured_image_path', value);
      toast.success('Image uploaded.');
    } catch (err) {
      toast.error(err.message || 'Upload failed. You can paste an image path instead.');
    } finally {
      setUploading(false);
    }
  }

  async function save(submitAfter) {
    setErrors({});
    setSaving(true);
    try {
      const payload = {
        ...form,
        // backend expects a nullable integer, not an empty string
        story_category_id: form.story_category_id ? Number(form.story_category_id) : null,
      };
      const saved = isEdit ? await storiesApi.update(id, payload) : await storiesApi.create(payload);
      if (submitAfter && saved?.id) {
        await storiesApi.submit(saved.id, 'Submitted for review.');
        toast.success('Story saved and submitted for review.');
      } else {
        toast.success(isEdit ? 'Story updated.' : 'Draft saved.');
      }
      navigate('/member/stories');
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || 'Could not save story.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Seo title={isEdit ? 'Edit Story' : 'New Story'} />
      <h1 className="font-display text-2xl font-bold">{isEdit ? 'Edit Story' : 'Submit a Story'}</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save(false);
        }}
        className="card space-y-5 p-6"
      >
        <Field label="Title" error={errors.title?.[0]}>
          <input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} required />
        </Field>

        <Field label="Excerpt" hint="A short summary shown on cards and in search results." error={errors.excerpt?.[0]}>
          <textarea
            className="input"
            rows={2}
            value={form.excerpt}
            onChange={(e) => set('excerpt', e.target.value)}
          />
        </Field>

        <Field label="Category" error={errors.story_category_id?.[0]} hint="Helps readers find related stories.">
          <select
            className="input"
            value={form.story_category_id}
            onChange={(e) => set('story_category_id', e.target.value)}
          >
            <option value="">Uncategorized</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Body" hint="Format text, add links, insert images, and build tables." error={errors.body?.[0]}>
          <RichTextEditor
            value={form.body}
            onChange={(html) => set('body', html)}
            placeholder="Tell the story…"
          />
        </Field>

        <Field label="Featured image" error={errors.featured_image_path?.[0]} hint="Upload an image or paste an existing path.">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input type="file" accept="image/*" onChange={handleImage} className="text-sm" />
            <input
              className="input"
              placeholder="stories/my-image.jpg"
              value={form.featured_image_path}
              onChange={(e) => set('featured_image_path', e.target.value)}
            />
          </div>
          {uploading && <p className="mt-1 text-xs text-ink-muted">Uploading…</p>}
          {form.featured_image_path && (
            <img
              src={assetUrl(form.featured_image_path)}
              alt="Featured preview"
              className="mt-3 aspect-video w-full max-w-sm rounded-lg object-cover shadow-card"
            />
          )}
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={(e) => set('is_featured', e.target.checked)}
            className="rounded border-gray-300 text-maroon-700 focus:ring-maroon-400"
          />
          Request to feature this story
        </label>

        <div className="flex flex-wrap gap-3 border-t border-cream-300 pt-5">
          <button type="submit" className="btn-outline" disabled={saving}>
            {saving ? <Spinner className="h-4 w-4" /> : 'Save Draft'}
          </button>
          <button type="button" onClick={() => save(true)} className="btn-primary" disabled={saving}>
            Save &amp; Submit for Review
          </button>
          <button type="button" onClick={() => navigate('/member/stories')} className="btn-ghost ml-auto">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
